import { useState, useEffect } from 'react';
import { apiUrl } from './api-url';

interface Usage {
  plan: string;
  emails: {
    sent: number;
    limit: number;
    unlimited: boolean;
    percentage: number;
  };
  contacts: {
    count: number;
    limit: number;
    unlimited: boolean;
    percentage: number;
  };
  campaigns: {
    today: number;
    daily_limit: number;
    unlimited: boolean;
    percentage: number;
  };
}

export function useUsage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(apiUrl('/api/v1/billing/usage'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch usage');
    } finally {
      setIsLoading(false);
    }
  };

  return { usage, isLoading, error, refresh: fetchUsage };
}

export function UsageMeter({ label, current, limit, unlimited, percentage }: {
  label: string;
  current: number;
  limit: number;
  unlimited: boolean;
  percentage: number;
}) {
  const isNearLimit = percentage > 80;
  const isOverLimit = percentage >= 100 && !unlimited;

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className={`text-sm ${isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-500'}`}>
          {unlimited ? '\u221E' : `${current.toLocaleString()} / ${limit.toLocaleString()}`}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`h-2.5 rounded-full transition-all duration-500 ${
            isOverLimit ? 'bg-red-600' : isNearLimit ? 'bg-yellow-500' : 'bg-blue-600'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        ></div>
      </div>
      {isNearLimit && !unlimited && (
        <p className="text-xs text-yellow-600 mt-1">
          You're approaching your limit. Consider upgrading your plan.
        </p>
      )}
    </div>
  );
}
